/**
 * A failed AI turn, shown as a failure.
 *
 * The chat rendered a failed turn two ways, both wrong. If the backend streamed
 * an error payload as the answer text, it arrived in a normal assistant bubble —
 * same style, same font, same alignment as a real answer — so a raw exception
 * shipped as "the answer". And the separate `stream.error` was a bare 12px red
 * line with the upstream message in it, no framing and no way to try again
 * (QA ledger ASK-002).
 *
 * This says what happened in a sentence a non-technical reader can act on,
 * keeps the technical detail available but secondary, and offers the retry that
 * is almost always the right next step.
 */
import { useState } from 'react';

export interface TurnErrorProps {
  /** The raw message from the stream or the upstream service. */
  detail: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

/**
 * Does this "answer" look like a raw error payload rather than a reply?
 *
 * A model that fails mid-call can return its provider's JSON error object as
 * the message body. It is still assistant text as far as the transport is
 * concerned, so nothing downstream would otherwise treat it as a failure.
 */
export const looksLikeRawError = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== 'object') return false;
    const record = parsed as Record<string, unknown>;
    // An answer is prose. An object whose top level is an error envelope is not.
    return 'error' in record || ('code' in record && 'message' in record);
  } catch {
    return false;
  }
};

export function TurnError({ detail, onRetry, isRetrying = false }: TurnErrorProps) {
  const [showDetail, setShowDetail] = useState(false);
  return (
    <div
      role="alert"
      style={{
        border: '1px solid var(--destructive-border, var(--destructive))',
        background: 'var(--destructive-subtle, transparent)',
        borderRadius: 8,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600 }}>That didn&rsquo;t work</div>
      <div style={{ fontSize: 12, color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
        LognAI couldn&rsquo;t answer this one. It&rsquo;s usually temporary — try again, or rephrase
        the question.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            style={{
              fontSize: 12,
              padding: '4px 10px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--surface, transparent)',
              cursor: isRetrying ? 'default' : 'pointer',
            }}
          >
            {isRetrying ? 'Retrying…' : 'Try again'}
          </button>
        )}
        {detail && (
          <button
            type="button"
            onClick={() => setShowDetail((open) => !open)}
            style={{
              fontSize: 12,
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--muted-foreground)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {showDetail ? 'Hide details' : 'Show details'}
          </button>
        )}
      </div>
      {/* Kept, but behind a click: useful when reporting the problem, noise
          otherwise, and never the thing presented as the answer. */}
      {showDetail && detail && (
        <pre
          style={{
            fontSize: 11,
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--muted-foreground)',
            maxHeight: 160,
            overflow: 'auto',
          }}
        >
          {detail}
        </pre>
      )}
    </div>
  );
}

export default TurnError;
